import axios from "axios";
import {API_URL} from "../constants";
import {authHeader} from "../Utilities";

type KHExtensionDataMap = {
    json: any;
    component: {
        key: string;
        render: {
            type: string;
            args: any[];
        };
    };
};

export type KHExtensionType = keyof KHExtensionDataMap;

export type KHExtension<K extends KHExtensionType> = {
    id: string;
    type: K;
    scope: string;
    description?: string;
    data: KHExtensionDataMap[K];
    private_data?: any;
    version: number;
};

export type KHExtensionAny = {
    [K in KHExtensionType]: KHExtension<K>;
}[KHExtensionType];

export function filterByKHExtType<K extends KHExtensionType>(type: K) {
    return (ext: KHExtensionAny): ext is Extract<KHExtensionAny, KHExtension<K>> => ext.type === type;
}

export type NewKHExtension = Omit<KHExtensionAny, 'id'>;

class KHExtensionsService {
    async fetchAllExtensions(wsId: string): Promise<KHExtensionAny[]> {
        const response = await axios.get<KHExtensionAny[]>(
            API_URL + `/workspaces/${wsId}/extensions`,
            {headers: authHeader()}
        );
        return response.data;
    }

    async fetchPublicExtensions<K extends KHExtensionType>(wsId: string, scope: string, v: number, type: K): Promise<KHExtension<K>[]> {
        const response = await axios.post<KHExtension<K>[]>(
            API_URL + `/workspaces/${wsId}/extensions/public`,
            {
                scope: scope,
                type: type,
                version: v
            },
            {headers: authHeader()}
        );
        return response.data;
    }

    async fetchComponents(wsId: string, scope: string, v: number): Promise<KHExtension<"component">[]> {
        const extensions = await this.fetchPublicExtensions(wsId, scope, v, "component");
        return extensions;
    }

    async fetchExtensionById(wsId: string, extensionId: string): Promise<KHExtensionAny> {
        const response = await axios.get<KHExtensionAny>(
            API_URL + `/workspaces/${wsId}/extensions/${extensionId}`,
            {headers: authHeader()}
        );
        return response.data;
    }

    async addExtension(wsId: string, extension: NewKHExtension): Promise<KHExtensionAny> {
        const response = await axios.post<KHExtensionAny>(
            API_URL + `/workspaces/${wsId}/extensions`,
            extension,
            {headers: authHeader()}
        );
        return response.data;
    }

    async updateExtension(wsId: string, extension: KHExtensionAny): Promise<KHExtensionAny> {
        const response = await axios.put<KHExtensionAny>(
            API_URL + `/workspaces/${wsId}/extensions/${extension.id}`,
            extension,
            {headers: authHeader()}
        );
        return response.data;
    }

    async deleteExtension(wsId: string, extensionId: string): Promise<void> {
        await axios.delete(
            API_URL + `/workspaces/${wsId}/extensions/${extensionId}`,
            {headers: authHeader()}
        );
    }
}

export default new KHExtensionsService();
